import { useState } from 'react';

interface TrendingTopic {
  id: number;
  query: string;
  category: string;
  categoryColor: string;
  icon: string;
  searches: string;
  trend: 'hot' | 'rising' | 'new';
}

const TRENDING_TOPICS: TrendingTopic[] = [
  {
    id: 1,
    query: 'GPT-5 capabilities and real-world benchmarks',
    category: 'AI & Tech',
    categoryColor: 'text-violet-700 bg-violet-100',
    icon: 'ri-robot-line',
    searches: '142K searches',
    trend: 'hot',
  },
  {
    id: 2,
    query: 'CRISPR gene therapy clinical trial results 2025',
    category: 'Biotech',
    categoryColor: 'text-emerald-700 bg-emerald-100',
    icon: 'ri-dna-line',
    searches: '89K searches',
    trend: 'hot',
  },
  {
    id: 3,
    query: 'EU AI Act compliance requirements for businesses',
    category: 'Policy',
    categoryColor: 'text-amber-700 bg-amber-100',
    icon: 'ri-government-line',
    searches: '76K searches',
    trend: 'rising',
  },
  {
    id: 4,
    query: 'Quantum computing error correction breakthroughs',
    category: 'Physics',
    categoryColor: 'text-cyan-700 bg-cyan-100',
    icon: 'ri-atom-line',
    searches: '63K searches',
    trend: 'rising',
  },
  {
    id: 5,
    query: 'Nuclear fusion energy milestone net energy gain',
    category: 'Energy',
    categoryColor: 'text-orange-700 bg-orange-100',
    icon: 'ri-sun-line',
    searches: '58K searches',
    trend: 'hot',
  },
  {
    id: 6,
    query: 'Mars mission 2026 crew selection and timeline',
    category: 'Space',
    categoryColor: 'text-indigo-700 bg-indigo-100',
    icon: 'ri-rocket-line',
    searches: '51K searches',
    trend: 'new',
  },
  {
    id: 7,
    query: 'Large language model hallucination reduction techniques',
    category: 'Research',
    categoryColor: 'text-rose-700 bg-rose-100',
    icon: 'ri-brain-line',
    searches: '44K searches',
    trend: 'rising',
  },
  {
    id: 8,
    query: 'mRNA cancer vaccine phase 3 trial outcomes',
    category: 'Medicine',
    categoryColor: 'text-teal-700 bg-teal-100',
    icon: 'ri-heart-pulse-line',
    searches: '37K searches',
    trend: 'new',
  },
];

const TREND_BADGE: Record<string, { label: string; style: string }> = {
  hot: { label: 'HOT', style: 'bg-rose-500 text-white' },
  rising: { label: 'RISING', style: 'bg-amber-500 text-white' },
  new: { label: 'NEW', style: 'bg-emerald-500 text-white' },
};

const TAG_PALETTE = [
  'bg-violet-100 text-violet-700',
  'bg-cyan-100 text-cyan-700',
  'bg-amber-100 text-amber-700',
  'bg-emerald-100 text-emerald-700',
  'bg-rose-100 text-rose-700',
  'bg-indigo-100 text-indigo-700',
  'bg-orange-100 text-orange-700',
  'bg-teal-100 text-teal-700',
];

interface Props {
  onSearch: (query: string) => void;
  wordsUsed: number;
  freeLimit: number;
  todaySearchCount: number;
  topTopics: string[];
}

export default function TrendingSearches({ onSearch, wordsUsed, freeLimit, todaySearchCount, topTopics }: Props) {
  const [hovered, setHovered] = useState<number | null>(null);

  const pct = Math.min(Math.round((wordsUsed / freeLimit) * 100), 100);
  const barColor = pct >= 100 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
  const remainingWords = Math.max(0, freeLimit - wordsUsed);

  return (
    <div className="max-w-4xl mx-auto px-6 pb-10">
      {/* Section header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center">
            <i className="ri-fire-line text-white text-base"></i>
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900">Trending Searches</h3>
            <p className="text-xs text-slate-500">Updated in real-time · Click any topic to search instantly</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 border border-rose-200 rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse"></span>
          <span className="text-xs font-semibold text-rose-700">Live</span>
        </div>
      </div>

      {/* Topics grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {TRENDING_TOPICS.map((topic) => (
          <button
            key={topic.id}
            onClick={() => onSearch(topic.query)}
            onMouseEnter={() => setHovered(topic.id)}
            onMouseLeave={() => setHovered(null)}
            className={`group flex items-start gap-3 p-4 bg-white rounded-xl border text-left transition-all duration-200 cursor-pointer ${
              hovered === topic.id
                ? 'border-violet-300 shadow-sm -translate-y-0.5'
                : 'border-slate-200 hover:border-violet-200'
            }`}
          >
            <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 transition-colors ${
              hovered === topic.id ? 'bg-gradient-to-br from-violet-500 to-fuchsia-500' : 'bg-slate-100'
            }`}>
              <i className={`${topic.icon} text-base ${hovered === topic.id ? 'text-white' : 'text-slate-600'}`}></i>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <p className={`text-sm font-semibold leading-snug transition-colors line-clamp-2 ${
                  hovered === topic.id ? 'text-violet-700' : 'text-slate-800'
                }`}>
                  {topic.query}
                </p>
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold flex-shrink-0 ${TREND_BADGE[topic.trend].style}`}>
                  {TREND_BADGE[topic.trend].label}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${topic.categoryColor}`}>
                  {topic.category}
                </span>
                <span className="text-[10px] text-slate-400 flex items-center gap-1">
                  <i className="ri-search-line text-[9px]"></i>
                  {topic.searches}
                </span>
              </div>
            </div>

            <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
              hovered === topic.id ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-1'
            }`}>
              <i className="ri-arrow-right-line text-violet-500 text-sm"></i>
            </div>
          </button>
        ))}
      </div>

      {/* Quick pill row */}
      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <span className="text-xs text-slate-400 font-medium">Quick topics:</span>
        {['AI Safety', 'Climate Data', 'Brain-Computer Interface', 'Autonomous Vehicles', 'Longevity Research', 'Dark Matter'].map((pill) => (
          <button
            key={pill}
            onClick={() => onSearch(pill + ' latest research 2025')}
            className="px-3 py-1.5 bg-slate-100 hover:bg-violet-100 hover:text-violet-700 border border-slate-200 hover:border-violet-300 rounded-full text-xs font-medium text-slate-600 transition-all cursor-pointer whitespace-nowrap"
          >
            {pill}
          </button>
        ))}
      </div>

      {/* ── Usage Stats Mini-Card ── */}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {/* Card header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-slate-50">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <i className="ri-bar-chart-2-line text-white text-xs"></i>
            </div>
            <span className="text-xs font-bold text-slate-800">Your Search Stats</span>
          </div>
          <span className="text-[10px] text-slate-400">Today &amp; all-time</span>
        </div>

        <div className="p-5">
          {/* Stat row */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            {/* Today searches */}
            <div className="text-center px-3 py-3 bg-violet-50 rounded-xl border border-violet-100">
              <div className="text-2xl font-black text-violet-700 mb-0.5 leading-none">
                {todaySearchCount}
              </div>
              <div className="text-[10px] text-violet-500 font-semibold leading-tight">
                searches<br />today
              </div>
            </div>

            {/* Words used */}
            <div className="text-center px-3 py-3 bg-slate-50 rounded-xl border border-slate-200">
              <div className="text-2xl font-black text-slate-800 mb-0.5 leading-none">
                {wordsUsed > 999 ? `${(wordsUsed / 1000).toFixed(1)}k` : wordsUsed}
              </div>
              <div className="text-[10px] text-slate-500 font-semibold leading-tight">
                words<br />used
              </div>
            </div>

            {/* Words left */}
            <div className={`text-center px-3 py-3 rounded-xl border ${pct >= 100 ? 'bg-red-50 border-red-100' : pct >= 70 ? 'bg-amber-50 border-amber-100' : 'bg-emerald-50 border-emerald-100'}`}>
              <div className={`text-2xl font-black mb-0.5 leading-none ${pct >= 100 ? 'text-red-700' : pct >= 70 ? 'text-amber-700' : 'text-emerald-700'}`}>
                {remainingWords > 999 ? `${(remainingWords / 1000).toFixed(1)}k` : remainingWords}
              </div>
              <div className={`text-[10px] font-semibold leading-tight ${pct >= 100 ? 'text-red-500' : pct >= 70 ? 'text-amber-500' : 'text-emerald-500'}`}>
                words<br />remaining
              </div>
            </div>
          </div>

          {/* Usage progress bar */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-semibold text-slate-600">Free plan usage</span>
              <span className={`text-[11px] font-bold ${pct >= 100 ? 'text-red-600' : pct >= 70 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {pct}%
              </span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ${barColor} ${pct >= 100 ? 'animate-pulse' : ''}`}
                style={{ width: `${pct}%` }}
              ></div>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-slate-400">0</span>
              <span className="text-[10px] text-slate-400">{freeLimit.toLocaleString()} free words</span>
            </div>
          </div>

          {/* Top topics tag cloud */}
          {topTopics.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2.5">
                <i className="ri-price-tag-3-line text-slate-500 text-xs"></i>
                <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wide">Your Top Topics</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {topTopics.map((topic, i) => (
                  <button
                    key={topic}
                    onClick={() => onSearch(topic + ' latest research')}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold cursor-pointer transition-all hover:scale-105 hover:shadow-sm whitespace-nowrap ${TAG_PALETTE[i % TAG_PALETTE.length]}`}
                  >
                    {topic}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 mt-2">Click any tag to search that topic instantly</p>
            </div>
          )}

          {topTopics.length === 0 && (
            <div className="flex items-center gap-2 py-2 px-3 bg-slate-50 rounded-lg border border-dashed border-slate-200">
              <i className="ri-price-tag-3-line text-slate-300 text-base"></i>
              <p className="text-xs text-slate-400">Your top topics appear here after a few searches</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
